import axios, { AxiosError } from 'axios';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import { noop } from 'lodash-es';

type AccessTokenGetter = () => string | null | undefined | Promise<string | null | undefined>;
type LocaleGetter = () => string | undefined | Promise<string | undefined>;
type AuthErrorHandler = (error: AxiosError) => void | Promise<void>;

export interface StreamOptions {
  signal?: AbortSignal;
  /** Overrides the transport's base URL (e.g. the BFF). */
  baseURL?: string;
}

/** A streaming request answered with a non-2xx status: `body` is the parsed JSON error, when there is one. */
export class StreamHttpError extends Error {
  constructor(
    readonly status: number,
    readonly body?: unknown,
  ) {
    super(`Request failed with status code ${status}`);
    this.name = 'StreamHttpError';
  }
}

export class Transport {
  private readonly axios: AxiosInstance;
  private accessTokenGetter?: AccessTokenGetter;
  private authErrorHandler?: AuthErrorHandler;
  private localeGetter?: LocaleGetter;

  constructor(private readonly baseUrl: string) {
    this.axios = axios.create({
      baseURL: baseUrl,
    });

    axiosRetry(this.axios, { retries: 3 });

    this.axios.interceptors.request.use(async (config) => {
      if (!config._skipAuth && this.accessTokenGetter) {
        const accessToken = await this.accessTokenGetter();

        if (accessToken) {
          config.headers = config.headers ?? {};
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
      }

      if (this.localeGetter) {
        const locale = await this.localeGetter();
        if (locale) {
          config.headers = config.headers ?? {};
          config.headers['Accept-Language'] = locale;
        }
      }

      return config;
    });

    this.axios.interceptors.response.use(undefined, async (error: AxiosError) => {
      const status = error.response?.status;

      if (status === 401 && this.authErrorHandler) {
        await this.authErrorHandler(error);

        // Swallow 401 errors after triggering logout to prevent
        // unhandled promise rejections across the app
        return new Promise(noop);
      }

      return Promise.reject(error);
    });
  }

  get<T = unknown>(url: string, config?: AxiosRequestConfig) {
    return this.axios.get<T>(url, config);
  }

  post<T = unknown>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.axios.post<T>(url, data, config);
  }

  put<T = unknown>(url: string, data: unknown, config?: AxiosRequestConfig) {
    return this.axios.put<T>(url, data, config);
  }

  patch<T = unknown>(url: string, data: unknown, config?: AxiosRequestConfig) {
    return this.axios.patch<T>(url, data, config);
  }

  delete<T = unknown>(url: string, config?: AxiosRequestConfig) {
    return this.axios.delete<T>(url, config);
  }

  /**
   * POSTs `body` as JSON and returns the response body for reading as a stream (SSE).
   * Same token, locale and 401 handling as the other methods, but with native `fetch` and no retries.
   */
  async stream(
    url: string,
    body: unknown,
    { signal, baseURL }: StreamOptions = {},
  ): Promise<ReadableStream<Uint8Array>> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'text/event-stream' };

    const accessToken = await this.accessTokenGetter?.();
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    const locale = await this.localeGetter?.();
    if (locale) {
      headers['Accept-Language'] = locale;
    }

    const base = (baseURL ?? this.baseUrl).replace(/\/+$/, '');
    const response = await fetch(`${base}/${url.replace(/^\/+/, '')}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body ?? {}),
      signal,
    });

    if (!response.ok) {
      const parsed: unknown = await response.json().catch(() => undefined);

      if (response.status === 401 && this.authErrorHandler) {
        await this.authErrorHandler(new AxiosError('Request failed with status code 401', AxiosError.ERR_BAD_REQUEST));
      }

      throw new StreamHttpError(response.status, parsed);
    }

    if (!response.body) {
      throw new StreamHttpError(response.status);
    }

    return response.body;
  }

  setAccessTokenGetter(getter: AccessTokenGetter) {
    this.accessTokenGetter = getter;
  }

  setLocaleGetter(getter: LocaleGetter) {
    this.localeGetter = getter;
  }

  setAuthErrorHandler(handler: AuthErrorHandler) {
    this.authErrorHandler = handler;
  }
}
